import Section from "@/components/settings/Section";
import InterestsSelector from "@/components/shared/InterestsSelector";
import { useAPI } from "@/contexts/APIContext";
import { useSettings } from "@/contexts/SettingsContext";
import handleError from "@/utils/handleError";
import { useEffect, useState } from "react";

interface InterestsSectionProps {
  initialInterests: string[];
}

export default function InterestsSection({ initialInterests }: InterestsSectionProps) {
  const { api, updateUser } = useAPI();
  const { setSuccessMessage, setErrorMessage } = useSettings();

  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [originalInterests, setOriginalInterests] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const minRequired = 5;

  // Initialize interests data
  useEffect(() => {
    if (!initialInterests?.length) {
      // Set 5 default interests if none found (the minimum required)
      const defaultInterests = ["Basic Phrases", "Travel", "Business", "Food", "Culture"];
      setSelectedInterests(defaultInterests);
      setOriginalInterests([...defaultInterests]);
      return;
    }

    setSelectedInterests(initialInterests);
    setOriginalInterests([...initialInterests]);
  }, [initialInterests]);

  // Check for changes
  useEffect(() => {
    if (selectedInterests.length !== originalInterests.length) {
      setIsChanged(true);
      return;
    }

    // Check if any interest has changed
    const changed =
      selectedInterests.some((interest) => !originalInterests.includes(interest)) ||
      originalInterests.some((interest) => !selectedInterests.includes(interest));

    setIsChanged(changed);
  }, [selectedInterests, originalInterests, minRequired]);

  const handleSave = async () => {
    if (selectedInterests.length < minRequired) return;

    setIsSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Update interests directly
      await api.user.update({ interests: selectedInterests });

      setOriginalInterests([...selectedInterests]);
      await updateUser(true);
      setSuccessMessage("Interests updated successfully");
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to update interests");
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInterestsChange = (interests: string[]) => {
    setSelectedInterests(interests);
  };

  return (
    <Section
      title="Interests"
      onSave={handleSave}
      isChanged={isChanged}
      isSaving={isSaving}
      disabled={selectedInterests.length < minRequired}
      id="interests"
    >
      <InterestsSelector
        selectedInterests={selectedInterests}
        onChange={handleInterestsChange}
        minRequired={minRequired}
      />
    </Section>
  );
}
